export default function PrivacyPolicy() {
  const sections = [
    {
      title: "1. Information We Collect",
      content: (
        <>
          <p className="text-gray-300 mb-3">
            We collect information you provide directly to us in the following situations:
          </p>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">▸</span>
              <span className="text-gray-300">
                <strong className="text-white">Contact Forms:</strong> When you reach out
                via our website, we collect your name, email address, phone number (if
                provided), and message content.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">▸</span>
              <span className="text-gray-300">
                <strong className="text-white">Online Orders &amp; Delivery:</strong> When
                you place an order, we collect your name, delivery address, contact number,
                email address, and payment details (processed securely through our payment
                provider).
              </span>
            </li>
          </ul>
        </>
      ),
    },
    {
      title: "2. How We Use Your Information",
      content: (
        <ul className="space-y-3">
          {[
            "To respond to your enquiries and provide customer support",
            "To process and fulfil your orders and arrange delivery",
            "To send you order confirmations and delivery updates",
            "To improve our website and services",
            "To comply with applicable legal obligations",
          ].map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-red-500 mt-1">▸</span>
              <span className="text-gray-300">{item}</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: "3. Sharing Your Information",
      content: (
        <>
          <p className="text-gray-300 mb-3">
            We do not sell or rent your personal information to third parties. We may
            share your data only in the following circumstances:
          </p>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">▸</span>
              <span className="text-gray-300">
                <strong className="text-white">Delivery Partners:</strong> Your name,
                address, and contact number are shared solely to fulfil your order.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">▸</span>
              <span className="text-gray-300">
                <strong className="text-white">Payment Processors:</strong> Payment details
                are handled securely by our third-party provider and never stored on our
                servers.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-red-500 mt-1">▸</span>
              <span className="text-gray-300">
                <strong className="text-white">Legal Requirements:</strong> We may disclose
                information if required by law or valid government authority requests.
              </span>
            </li>
          </ul>
        </>
      ),
    },
    {
      title: "4. Data Retention",
      content: (
        <p className="text-gray-300">
          We retain your personal data only for as long as necessary to fulfil the
          purposes outlined in this policy, or as required by law. Order and delivery
          data is typically retained for up to 2 years for accounting and legal purposes.
        </p>
      ),
    },
    {
      title: "5. Cookies",
      content: (
        <p className="text-gray-300">
          Our website may use basic cookies to ensure the site functions correctly. We
          do not use tracking or advertising cookies. You can control cookie settings
          through your browser preferences at any time.
        </p>
      ),
    },
    {
      title: "6. Your Rights",
      content: (
        <>
          <p className="text-gray-300 mb-3">You have the right to:</p>
          <ul className="space-y-3">
            {[
              "Access the personal data we hold about you",
              "Request correction of inaccurate or incomplete data",
              "Request deletion of your personal data",
              "Withdraw consent at any time where processing is based on consent",
              "Lodge a complaint with the relevant data protection authority",
            ].map((item, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-red-500 mt-1">▸</span>
                <span className="text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </>
      ),
    },
    {
      title: "7. Data Security",
      content: (
        <p className="text-gray-300">
          We implement appropriate technical and organisational measures to protect your
          personal information against unauthorised access, alteration, disclosure, or
          destruction. However, no method of transmission over the internet is 100%
          secure, and we cannot guarantee absolute security.
        </p>
      ),
    },
  ];

  return (
    <>
      {/* Spacer for fixed navbar height */}
      <div className="h-28" />

      <div className="min-h-screen px-6 py-16 max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-12 text-center">
          <p className="text-red-500 text-sm font-semibold tracking-widest uppercase mb-3">
            Legal
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-gray-400 text-sm">Last updated: March 2026</p>
          <div className="mt-6 h-1 w-20 bg-red-600 mx-auto rounded-full" />
        </div>

        {/* Intro */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8 backdrop-blur-sm">
          <p className="text-gray-300 leading-relaxed">
            Welcome to <strong className="text-white">Galaxy Supermarket</strong>,
            operated by <strong className="text-white">PMD Supermarket LLP</strong>. We
            are committed to protecting your personal information and your right to
            privacy. This policy explains what information we collect, how we use it,
            and what rights you have in relation to it.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {sections.map((section, i) => (
            <div
              key={i}
              className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm hover:border-red-500/30 transition-colors duration-300"
            >
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-red-600 rounded-full inline-block" />
                {section.title}
              </h2>
              {section.content}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-6 bg-gradient-to-br from-red-900/40 to-black/40 border border-red-500/30 rounded-2xl p-6 backdrop-blur-sm">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
            <span className="w-2 h-6 bg-red-600 rounded-full inline-block" />
            8. Contact Us
          </h2>
          <p className="text-gray-300 mb-4">
            If you have any questions about this Privacy Policy or wish to exercise your
            rights, please contact us:
          </p>
          <div className="space-y-2 text-gray-300">
            <p><strong className="text-white">Galaxy Supermarket</strong></p>
            <p>Operated by PMD Supermarket LLP</p>
            <p>
              📧{" "}
              <a
                href="mailto:pmdsupermarket@gmail.com"
                className="text-red-400 hover:text-red-300 transition underline"
              >
                pmdsupermarket@gmail.com
              </a>
            </p>
            <p>
              📞{" "}
              <a
                href="tel:+919623969912"
                className="text-red-400 hover:text-red-300 transition underline"
              >
                9623969912
              </a>
            </p>
            <p>
              💬{" "}
              <a
                href="https://wa.me/919623969912"
                className="text-red-400 hover:text-red-300 transition underline"
              >
                WhatsApp: 9623969912
              </a>
            </p>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-gray-600 text-sm mt-12 border-t border-white/10 pt-6">
          © {new Date().getFullYear()} PMD Supermarket LLP. All rights reserved.
        </p>
      </div>
    </>
  );
}
